import React, { useState } from "react";
import styled from "styled-components";
import { addDoc, collection } from "firebase/firestore";
import { db } from "./Firebase";
import { useTranslation } from "react-i18next";
import { EstiloButton } from "./App";

const Container = styled.div`
  max-width: 600px;
  margin: 20px auto;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;
const Header = styled.h2`
  text-align: center;
`;
const FormGroup = styled.div`
  margin-bottom: 15px;
`;
const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
`;
const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;
const Textarea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;
const OptionsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin: 10px 0;
  flex-wrap: wrap;
`;
const OptionButton = styled.button`
  flex: 1;
  padding: 10px;
  background-color: ${(props) => (props.selected ? "#c20000" : "#f5f5f5")}; /* Rojo oscuro si está seleccionado, gris claro si no */
  color: ${(props) => (props.selected ? "white" : "#c20000")}; /* Texto blanco si está seleccionado, rojo oscuro si no */
  border: 1px solid #c20000; /* Borde rojo */
  border-radius: 5px; /* Bordes ligeramente redondeados */
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${(props) => (props.selected ? "#a80000" : "#ffe6e6")}; /* Rojo más oscuro si está seleccionado, rosa claro si no */
    color: ${(props) => (props.selected ? "white" : "#c20000")}; /* Mantiene el texto blanco o rojo oscuro */
  }
  &:active {
    background-color: #a80000; /* Rojo más oscuro en clic */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2); /* Sombra al hacer clic */
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const SatisfactionSurvey = () => {
  const { t } = useTranslation();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    fechaServicio: "",
    origenDestino: "",
    puntualidad: "",
    limpiezaBus: "",
    profesionalismo: "",
    comodidad: "",
    calidadGeneral: "",
    comentarios: "",
    recomendar: "",
  });

  const [missingFields, setMissingFields] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const optionsMapping = {
    "Muy Satisfecho": "Muy Satisfecho",
    "Satisfecho": "Satisfecho",
    "Neutral": "Neutral",
    "Insatisfecho": "Insatisfecho",
    "Muy Insatisfecho": "Muy Insatisfecho",
    "Very Satisfied": "Muy Satisfecho",
    "Satisfied": "Satisfecho",
    "Dissatisfied": "Insatisfecho",
    "Very Dissatisfied": "Muy Insatisfecho",
    "Muito Satisfeito": "Muy Satisfecho",
    "Satisfeito": "Satisfecho",
    "Neutro": "Neutral",
    "Insatisfeito": "Insatisfecho",
    "Muito Insatisfeito": "Muy Insatisfecho",
  };

  const handleOptionChange = (field, value) => {
    // Mapea el valor seleccionado al equivalente en español
    const translatedValue = optionsMapping[value] || value;
    setFormData({ ...formData, [field]: translatedValue });
  };

  const validateStep = () => {
    const requiredFields = {
      1: ["nombre", "email", "telefono", "fechaServicio", "origenDestino"],
      2: ["puntualidad"],
      3: ["limpiezaBus"],
      4: ["profesionalismo"],
      5: ["comodidad"],
      6: ["calidadGeneral"],
      7: [],
      8: ["recomendar"],
    };
    const fieldsToCheck = requiredFields[step];
    const emptyFields = fieldsToCheck.filter((field) => !formData[field]);

    if (emptyFields.length > 0) {
      setMissingFields(emptyFields);
      setIsModalOpen(true);
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setStep((prevStep) => prevStep + 1);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      await addDoc(collection(db, "satisfaccion_servicio"), formData);
      setStep(1);
      setFormData({
        nombre: "",
        email: "",
        telefono: "",
        fechaServicio: "",
        origenDestino: "",
        puntualidad: "",
        limpiezaBus: "",
        profesionalismo: "",
        comodidad: "",
        calidadGeneral: "",
        comentarios: "",
        recomendar: "",
      });

      // Reemplazar los saltos de línea por <br /> en comentarios
      const comentariosFormateado = formData.comentarios.replace(
        /\n/g,
        "<br />"
      );

      // Añadir un nuevo documento a la colección 'mail' para enviar el correo electrónico
      await addDoc(collection(db, "mail"), {
        to: [
          "lobos_claudio@catainternacional.com",
          "ramos_fernando@catainternacional.com",
          "estarriola_elio@catainternacional.com",
          "james_carina@catainternacional.com",
          "arissa_marcelo@transportesbmitre.com.ar",
          "sanchez_marisa@catainternacional.com",
          "mendoza_dairelys@catainternacional.com",
          "carmona_juliana@catainternacional.com",
          "rojas_dario@catainternacional.com",
          "director@catainternacional.com",
        ],
        from: `"Encuesta de Satisfacción" <rojas_dario@catainternacional.com>`, // 👈 este es el remitente visible
        message: {
          subject: "Servicio Especial:  " + formData.nombre,
          text: `Servicio del día ${new Date(
            new Date(formData.fechaServicio).toLocaleString("en-US", {
              timeZone: "UTC",
            })
          ).toLocaleDateString("es-ES")}`,
          html: `<p style="margin: 0px; font-weight: bold;">Devolucion del cliente mediante la encuesta de satisfaccion con los siguientes datos:</p>
            <br>
         <p style="margin: 0px; font-weight: bold;">Cliente: ${
           formData.nombre
         }</p>
         <p style="margin: 0px;">Email: ${formData.email}</p>
         <p style="margin: 0px;">Teléfono: ${formData.telefono}</p>
         <p style="margin: 0px;">Fecha del servicio: ${new Date(
           new Date(formData.fechaServicio).toLocaleString("en-US", {
             timeZone: "UTC",
           })
         ).toLocaleDateString("es-ES")}</p>
         <p style="margin: 0px;">Origen y destino: ${formData.origenDestino}</p>
         <p style="margin: 0px;">Puntualidad del Servicio: ${
           formData.puntualidad
         }</p>
         <p style="margin: 0px;">Estado y Limpieza del Bus: ${
           formData.limpiezaBus
         }</p>
         <p style="margin: 0px;">Profesionalismo del Chofer: ${
           formData.profesionalismo
         }</p>
         <p style="margin: 0px;">Comodidad del Viaje: ${formData.comodidad}</p>
         <p style="margin: 0px;">Calidad del Servicio en General: ${
           formData.calidadGeneral
         }</p>
         <br>
         <p style="margin: 0px; font-weight: bold;">¿Hubo algún aspecto del servicio que le gustaría mejorar?</p>
         <p style="margin: 0px;">${comentariosFormateado}</p>
         <p style="margin: 0px; font-weight: bold;">¿Recomendaría nuestro servicio a otros?</p>
         <p style="margin: 0px;">${formData.recomendar}</p>
         <br>
         <p style="margin: 0px; font-weight: bold;">Atte.</p>
         <p style="margin: 0px; font-weight: bold;">Equipo Comercial Serv. Esp.</p>
         <hr>
            <p style="margin: 0px; font-size: 12px; color: #555;">
              Este es un correo automático generado por el sistema de presupuestos de Servicios Especiales.
            </p>
         `,
        },
      });

      console.log("Queued email for delivery!");
    } catch (error) {
      console.error("Error al guardar la encuesta:", error);
      alert(
        "Hubo un error al enviar la encuesta. Por favor, inténtalo de nuevo."
      );
    }
  };

  const questions = [
    {
      step: 2,
      label: t("SatisfactionSurvey.questions.labelStep2"),
      field: "puntualidad",
      options: [
        t("SatisfactionSurvey.options.step1"),
        t("SatisfactionSurvey.options.step2"),
        t("SatisfactionSurvey.options.step3"),
        t("SatisfactionSurvey.options.step4"),
        t("SatisfactionSurvey.options.step5"),
      ],
    },
    {
      step: 3,
      label: t("SatisfactionSurvey.questions.labelStep3"),
      field: "limpiezaBus",
      options: [
        t("SatisfactionSurvey.options.step1"),
        t("SatisfactionSurvey.options.step2"),
        t("SatisfactionSurvey.options.step3"),
        t("SatisfactionSurvey.options.step4"),
        t("SatisfactionSurvey.options.step5"),
      ],
    },
    {
      step: 4,
      label: t("SatisfactionSurvey.questions.labelStep4"),
      field: "profesionalismo",
      options: [
        t("SatisfactionSurvey.options.step1"),
        t("SatisfactionSurvey.options.step2"),
        t("SatisfactionSurvey.options.step3"),
        t("SatisfactionSurvey.options.step4"),
        t("SatisfactionSurvey.options.step5"),
      ],
    },
    {
      step: 5,
      label: t("SatisfactionSurvey.questions.labelStep5"),
      field: "comodidad",
      options: [
        t("SatisfactionSurvey.options.step1"),
        t("SatisfactionSurvey.options.step2"),
        t("SatisfactionSurvey.options.step3"),
        t("SatisfactionSurvey.options.step4"),
        t("SatisfactionSurvey.options.step5"),
      ],
    },
    {
      step: 6,
      label: t("SatisfactionSurvey.questions.labelStep6"),
      field: "calidadGeneral",
      options: [
        t("SatisfactionSurvey.options.step1"),
        t("SatisfactionSurvey.options.step2"),
        t("SatisfactionSurvey.options.step3"),
        t("SatisfactionSurvey.options.step4"),
        t("SatisfactionSurvey.options.step5"),
      ],
    },
  ];

  return (
    <Container>
      {step === 9 ? (
        <Header>{t("SatisfactionSurvey.header1")}</Header>
      ) : (
        <Header>{t("SatisfactionSurvey.header2")}</Header>
      )}

      {step === 1 && (
        <>
          <FormGroup>
            <Label>{t("SatisfactionSurvey.formGroup.nombre")}</Label>
            <Input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("SatisfactionSurvey.formGroup.email")}</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("SatisfactionSurvey.formGroup.telefono")}</Label>
            <Input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("SatisfactionSurvey.formGroup.fechaServicio")}</Label>
            <Input
              type="date"
              name="fechaServicio"
              value={formData.fechaServicio}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("SatisfactionSurvey.formGroup.origenDestino")}</Label>
            <Textarea
              name="origenDestino"
              value={formData.origenDestino}
              onChange={handleInputChange}
            />
          </FormGroup>
          <EstiloButton onClick={handleNextStep}>
            {t("SatisfactionSurvey.button.siguiente")}
          </EstiloButton>
        </>
      )}
      {questions
        .filter((q) => q.step === step)
        .map((q) => (
          <div key={q.step}>
            <FormGroup>
              <Label>{q.label}</Label>
              <OptionsContainer>
                {q.options.map((option) => (
                  <OptionButton
                    key={option}
                    selected={formData[q.field] === optionsMapping[option]}
                    onClick={() => handleOptionChange(q.field, option)}
                  >
                    {option}
                  </OptionButton>
                ))}
              </OptionsContainer>
            </FormGroup>
            <EstiloButton onClick={handleNextStep}>
              {t("SatisfactionSurvey.button.siguiente")}
            </EstiloButton>
          </div>
        ))}
      {step === 7 && (
        <>
          <FormGroup>
            <Label>{t("SatisfactionSurvey.formGroup.label7")}</Label>
            <Textarea
              name="comentarios"
              value={formData.comentarios}
              onChange={handleInputChange}
              rows="4"
              placeholder={t("SatisfactionSurvey.formGroup.placeholder7")}
            />
          </FormGroup>
          <EstiloButton onClick={handleNextStep}>
            {t("SatisfactionSurvey.button.siguiente")}
          </EstiloButton>
        </>
      )}
      {step === 8 && (
        <>
          <FormGroup>
            <Label>{t("SatisfactionSurvey.formGroup.label8")}</Label>
            <select
              name="recomendar"
              value={formData.recomendar}
              onChange={handleInputChange}
            >
              <option value="" disabled>
                {t("SatisfactionSurvey.formGroup.optionStep8.option1")}
              </option>
              <option value="Sí">
                {t("SatisfactionSurvey.formGroup.optionStep8.option2")}
              </option>
              <option value="No">
                {t("SatisfactionSurvey.formGroup.optionStep8.option3")}
              </option>
            </select>
          </FormGroup>
          <EstiloButton onClick={handleNextStep}>
            {t("SatisfactionSurvey.button.siguiente")}
          </EstiloButton>
        </>
      )}
      {step === 9 && (
        <>
          <FormGroup>
            <Label>{t("SatisfactionSurvey.formGroup.label9")}</Label>
          </FormGroup>
          <EstiloButton onClick={handleSubmit}>
            {t("SatisfactionSurvey.button.enviar")}
          </EstiloButton>
        </>
      )}
      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <h6>{t("SatisfactionSurvey.alertMessage")}</h6>
            <div style={{ marginBottom: "8px" }}>
              {missingFields.map((field) => (
                <p key={field}>{t(`SatisfactionSurvey.formGroup.${field}`)}</p>
              ))}
            </div>
            <EstiloButton onClick={handleModalClose}>OK</EstiloButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default SatisfactionSurvey;
